<script setup lang="ts">

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'

import { OrderRelationLink } from '@shared/ui/links'
import { BadgeSurveyStatus } from '@shared/ui/badges'
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { AppIcon } from '@shared/ui/icons'

import SurveyAnswers from './SurveyAnswers.vue'
import { useTableSurveys } from '../model/table-surveys.hook'

defineOptions({
  name: 'TableSurveys',
})

const props = withDefaults(defineProps<{
  /** A discipline's Survey tab: every row is that discipline's, so its customer, kid and discipline columns go. */
  isDisciplinePage?: boolean
}>(), {
  isDisciplinePage: false,
})

const COLUMNS_COUNT = 9
const RELATION_COLUMNS_COUNT = 3

const refColumnsCount = computed<number>(() =>
  props.isDisciplinePage ? COLUMNS_COUNT - RELATION_COLUMNS_COUNT : COLUMNS_COUNT)

const {
  surveys,
  isLoading,
  hasSurveys,
  meta,
  perPageOptions,
  emptyCell,
  getTypeLabel,
  getCustomerName,
  getDisciplineLabel,
  getLessonId,
  getCreatedAt,
  getSourceLabel,
  getCustomerUrl,
  getKidUrl,
  getDisciplineSurveysUrl,
  isExpanded,
  getAnswersRowId,
  toggleAnswers,
  setChangePage,
  setChangePerPage,
} = useTableSurveys()
</script>

<template>
  <div class="table-surveys">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Survey type
            </AppText>
          </AppTh>
          <AppTh v-if="!isDisciplinePage">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Customer
            </AppText>
          </AppTh>
          <AppTh v-if="!isDisciplinePage">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Kid
            </AppText>
          </AppTh>
          <AppTh v-if="!isDisciplinePage">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Discipline
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Lesson ID
            </AppText>
          </AppTh>
          <AppTh class="table-surveys__nowrap">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Date
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Status
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Source
            </AppText>
          </AppTh>
          <AppTh class="table-surveys__toggle-cell" />
        </AppTr>
      </template>

      <template
        v-if="hasSurveys"
        #body
      >
        <template
          v-for="survey in surveys"
          :key="survey.id"
        >
          <AppTr>
            <AppTd class="medium">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ getTypeLabel(survey) }}
              </AppText>
            </AppTd>
            <AppTd
              v-if="!isDisciplinePage"
              class="medium"
            >
              <OrderRelationLink
                v-if="survey.customer"
                :href="getCustomerUrl(survey.customer.id)"
                :text="getCustomerName(survey.customer)"
                :notes="survey.customer.notes"
              />
              <AppText
                v-else
                name="body1"
                color="slate-900"
              >
                {{ emptyCell }}
              </AppText>
            </AppTd>
            <AppTd
              v-if="!isDisciplinePage"
              class="medium"
            >
              <OrderRelationLink
                v-if="survey.kid"
                :href="getKidUrl(survey.kid.id)"
                :text="survey.kid.name"
                :notes="survey.kid.notes"
              />
              <AppText
                v-else
                name="body1"
                color="slate-900"
              >
                {{ emptyCell }}
              </AppText>
            </AppTd>
            <AppTd
              v-if="!isDisciplinePage"
              class="medium"
            >
              <OrderRelationLink
                v-if="survey.discipline"
                :href="getDisciplineSurveysUrl(survey.discipline.id)"
                :text="getDisciplineLabel(survey.discipline)"
              />
              <AppText
                v-else
                name="body1"
                color="slate-900"
              >
                {{ emptyCell }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ getLessonId(survey) }}
              </AppText>
            </AppTd>
            <AppTd class="medium table-surveys__nowrap">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ getCreatedAt(survey) }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <BadgeSurveyStatus :status="survey.status" />
            </AppTd>
            <AppTd class="medium">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ getSourceLabel(survey) }}
              </AppText>
            </AppTd>
            <AppTd class="zero-padding table-surveys__toggle-cell">
              <button
                type="button"
                class="table-surveys__toggle"
                :class="{ 'table-surveys__toggle--open': isExpanded(survey) }"
                :aria-expanded="isExpanded(survey)"
                :aria-controls="getAnswersRowId(survey)"
                :aria-label="`Toggle answers for survey ${survey.id}`"
                @click="toggleAnswers(survey)"
              >
                <AppIcon
                  :icon="faChevronDown"
                  size="small"
                />
              </button>
            </AppTd>
          </AppTr>

          <AppTr
            v-if="isExpanded(survey)"
            :id="getAnswersRowId(survey)"
          >
            <AppTd
              class="zero-padding table-surveys__answers"
              :colspan="refColumnsCount"
            >
              <SurveyAnswers :answers="survey.answers" />
            </AppTd>
          </AppTr>
        </template>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableSurveys.scss" />
